import { ReactNode } from 'react';

import { faFolder } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { INodeRendererProps } from 'react-accessible-treeview';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowSvg } from 'assets/svg/arrow-right.svg';
import { ReactComponent as FolderOpenIconSVG } from 'assets/svg/folder-icon.svg';
import { ButtonVariants } from 'constants/common';
import { cn } from 'utils/global';

import { Button, Checkbox } from './index';

interface Props extends INodeRendererProps {
  children?: ReactNode;
}

const TreeViewItem = ({
  getNodeProps,
  level,
  isSelected,
  isBranch,
  handleExpand,
  isExpanded,
  element,
  handleSelect,
  isHalfSelected,
  children,
}: Props) => {
  const { t } = useTranslation();
  const isItemSelected = isSelected || isHalfSelected;

  return (
    <div style={{ marginLeft: `${14 * (level - 1)}px` }} data-cy="treeViewItem">
      <div
        {...getNodeProps({ onClick: () => '' })}
        className={cn(
          'flex items-center rounded transition-all cursor-pointer text-darkgray text-sm h-9 w-full my-1 border-transparent focus:border-transparent focus:ring-0',
          {
            'bg-primary-50': isItemSelected,
            'hover:bg-neutral-50 dark:hover:bg-neutral-900': !isItemSelected,
          }
        )}
      >
        <div className="flex items-center">
          {isBranch ? (
            <Button
              variant={ButtonVariants.PLAIN}
              type="button"
              onClick={handleExpand}
              className={
                'p-0 w-7 h-7 mr-2 flex items-center justify-center rounded-full hover:!bg-lightGray6 dark:!bg-transparent'
              }
            >
              <ArrowSvg
                className={cn('fill-neutral-600 dark:fill-neutral-400 transition-all', {
                  'rotate-90': isExpanded,
                })}
              />
            </Button>
          ) : (
            <div className="w-7 h-7 mr-2" />
          )}
          <Checkbox
            dataCy="treeViewItem-checkbox"
            inputClassName="dark:checked:border-white dark:border"
            checked={isItemSelected}
            onClick={handleSelect}
            readOnly
          />
          {isExpanded ? (
            <div className="mx-2">
              <FolderOpenIconSVG />
            </div>
          ) : (
            <FontAwesomeIcon className="mx-2 text-neutral-600 w-5 h-5" icon={faFolder} />
          )}
          <span
            className={cn('name text-bodyMd', {
              'text-primary-500': isSelected || isHalfSelected,
              'text-neutral-600 dark:text-neutral-400': !isSelected,
            })}
          >
            {t(element.name)}
          </span>
          {children}
        </div>
      </div>
    </div>
  );
};

export default TreeViewItem;
